import { useNavigate } from 'react-router-dom'

import {
  Box,
  CallToActionPad,
  Icon,
  Spinner,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { dateBackendToUI } from 'modules/core/lib/dates'
import { getCurrencySymbolFromCode } from 'modules/core/lib/formatCurrencyUtils'
import i18n from 'modules/core/lib/i18n'

interface Props {
  baseCurrency: string
  targetCurrency: string
  date: string
  rate?: number
  loading?: boolean
  canCustomizeSettings: boolean
}

const formatRate = (amount: number, currency: string, locale: string) => {
  const rate = new Intl.NumberFormat(locale, {
    maximumFractionDigits: 6,
  }).format(amount)

  return `${rate} ${currency} (${getCurrencySymbolFromCode(currency)})`
}

const ExchangeRateTooltip = ({
  baseCurrency,
  targetCurrency,
  rate,
  date,
  loading,
  canCustomizeSettings,
}: Props) => {
  const [open, toggle] = useToggle(false)
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const {
    access: { locale },
  } = useCurrentSession()

  const onClickCta = () =>
    navigate(
      navigation.settings.expenses.expensesForms.fields.editByIdentifier.fullPath({
        resource_type: 'expense',
        identifier: 'exchange_rate',
      })
    )

  if (baseCurrency === targetCurrency) return null

  return (
    <Tooltip.ItemList attachment="bottom center" open={open} toggle={toggle} size="auto">
      <Box
        gap="s8"
        alignItems="center"
        cursor="pointer"
        flexDirection="row"
        justifyContent="spaceBetween"
      >
        <Icon.Small icon="info" encircled="grey800" />
      </Box>
      <Box gap="s16" alignItems="center">
        <Text.Header3>
          {i18n.t('expenses_v2.forms.fields.exchange_rate.tooltip.title')}
        </Text.Header3>
        {loading ? (
          <Box justifyContent="center" alignItems="center">
            <Spinner size="s32" />
          </Box>
        ) : (
          <>
            <Box alignItems="center" gap="s8" gapDivider width="full">
              <Box
                flexDirection="row"
                justifyContent="spaceBetween"
                width="full"
                textAlign="right"
                gap="s16"
              >
                <Text.Normal>
                  {i18n.t('expenses_v2.forms.fields.exchange_rate.tooltip.rate')}
                </Text.Normal>
                <Text.Normal>
                  {rate != null
                    ? `${formatRate(1, baseCurrency, locale)} = ${formatRate(
                        rate,
                        targetCurrency,
                        locale
                      )}`
                    : i18n.t('expenses_v2.forms.fields.exchange_rate.tooltip.not_available')}
                </Text.Normal>
              </Box>

              <Box
                flexDirection="row"
                justifyContent="spaceBetween"
                width="full"
                textAlign="right"
                gap="s16"
              >
                <Text.Normal>
                  {i18n.t('expenses_v2.forms.fields.exchange_rate.tooltip.date')}
                </Text.Normal>
                <Text.Normal>{dateBackendToUI(date)}</Text.Normal>
              </Box>
            </Box>

            {canCustomizeSettings && (
              <CallToActionPad
                buttonLabel={i18n.t('expenses_v2.forms.fields.exchange_rate.tooltip.cta')}
                aria-label={i18n.t('expenses_v2.forms.fields.exchange_rate.tooltip.date')}
                onClick={onClickCta}
              />
            )}
          </>
        )}
      </Box>
    </Tooltip.ItemList>
  )
}

export default ExchangeRateTooltip
